/**
 * SelectionFooter - Minimal footer component for grid row selection
 *
 * Shows:
 * - Selection count when rows are selected
 * - Delete button with confirmation dialog
 * - Optional photo upload button (single selection only)
 *
 * Used with useInlineMassUpdate for a clean mass-update experience
 * where editing happens inline rather than through a separate panel.
 */

import AddAPhotoIcon from '@mui/icons-material/AddAPhoto'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import {
  GridApiPremium,
  GridFooterContainer,
  GridPagination,
  GridRowId,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium'
import { FloatingActionBar } from 'components/shared'
import React, { ComponentPropsWithoutRef, useCallback } from 'react'

interface SelectionFooterProps extends ComponentPropsWithoutRef<'div'> {
  /** Current selection model from the grid */
  rowSelectionModel: GridRowSelectionModel
  /** Set selection model function */
  setRowSelectionModel: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>
  /** Grid API ref */
  apiRef: React.RefObject<GridApiPremium>
  /** Called when delete button is clicked (should open confirmation) */
  onDeleteClick: (e: React.MouseEvent) => void
  /** Optional: Called when photo upload completes (single selection only) */
  onPhotoUpload?: (file: File, selectedId: string | number) => void
  /** Whether photo upload is available */
  showPhotoUpload?: boolean
  /** Label for what type of items are selected (e.g., "organizations", "tasks") */
  itemLabel?: string
  /** Whether server-side pagination is enabled */
  serverSideMode?: boolean
  /** Optional filter row counts to display */
  filterRowCount?: number
  /** Optional total row count */
  totalRowCount?: number
  /** Popover component for delete confirmation */
  DeleteConfirmationPopover?: React.ReactNode
}

export const SelectionFooter: React.FC<SelectionFooterProps> = ({
  rowSelectionModel,
  setRowSelectionModel,
  apiRef,
  onDeleteClick,
  onPhotoUpload,
  showPhotoUpload = false,
  itemLabel = 'items',
  serverSideMode = false,
  filterRowCount,
  totalRowCount,
  DeleteConfirmationPopover,
}) => {
  const selectionCount = rowSelectionModel?.ids?.size ?? 0
  const hasSelection = selectionCount > 0

  const clearSelection = useCallback(() => {
    try {
      // Clear selection via apiRef
      const ids = rowSelectionModel?.ids
      if (ids && typeof ids.forEach === 'function') {
        ids.forEach((id: GridRowId) => apiRef.current?.selectRow(id, false))
      }
      apiRef.current?.setRowSelectionModel({ ids: new Set(), type: 'include' })
    } catch {
      // Keep safe across versions
    }
  }, [apiRef, rowSelectionModel])

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file && onPhotoUpload && selectionCount === 1) {
        const selectedId = Array.from(rowSelectionModel.ids)[0]
        onPhotoUpload(file, selectedId)
      }
      // Reset input so same file can be selected again
      event.target.value = ''
    },
    [onPhotoUpload, rowSelectionModel, selectionCount]
  )

  const singularLabel = itemLabel.endsWith('s') ? itemLabel.slice(0, -1) : itemLabel
  const displayLabel = selectionCount === 1 ? singularLabel : itemLabel

  return (
    <GridFooterContainer
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 52,
        overflow: 'hidden',
        px: 2,
      }}
    >
      {hasSelection ? (
        <FloatingActionBar
          visible={true}
          onClose={clearSelection}
          styles={{
            width: 'auto',
          }}
          sx={{
            backgroundColor: 'background.section',
            backdropFilter: 'blur(10px)',
            flexWrap: 'nowrap',
            overflow: 'visible',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 0.5,
            }}
          >
            {/* Selection count */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={selectionCount}
                sx={{ color: 'rgb(24, 144, 255)', fontSize: 16, fontWeight: 'bold' }}
              />
              <Typography variant="body2" noWrap>
                {displayLabel} selected
              </Typography>
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Photo upload (single selection only) */}
              {showPhotoUpload && selectionCount === 1 && onPhotoUpload && (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    id="selection-footer-photo-upload"
                  />
                  <label htmlFor="selection-footer-photo-upload" style={{ cursor: 'pointer' }}>
                    <Tooltip title="Upload Photo" placement="top">
                      <IconButton component="span" size="small">
                        <AddAPhotoIcon sx={{ height: 22, width: 22 }} />
                      </IconButton>
                    </Tooltip>
                  </label>
                </>
              )}

              {/* Delete button */}
              <Tooltip
                title={selectionCount === 1 ? `Delete ${singularLabel}` : `Delete ${selectionCount} ${itemLabel}`}
                placement="top"
              >
                <IconButton onClick={onDeleteClick} color="error" size="small">
                  <DeleteForeverIcon sx={{ height: 22, width: 22 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </FloatingActionBar>
      ) : (
        <>
          {/* Left side: Row counts when no selection */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {filterRowCount !== undefined && totalRowCount !== undefined && (
              <Typography variant="body2" color="text.secondary">
                {filterRowCount === totalRowCount
                  ? `${totalRowCount} ${itemLabel}`
                  : `${filterRowCount} of ${totalRowCount} ${itemLabel}`}
              </Typography>
            )}
          </Box>

          {/* Right side: Pagination */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {serverSideMode && <GridPagination />}
          </Box>
        </>
      )}

      {/* Delete confirmation popover - rendered outside the conditional */}
      {DeleteConfirmationPopover}
    </GridFooterContainer>
  )
}

export default SelectionFooter
